import { useCallback, useEffect, useState, type ImgHTMLAttributes, type SyntheticEvent } from 'react'

import * as Dialog from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface GalleryItem {
  src: string
  alt?: string
  caption?: string
}

export interface ImagePreviewProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Full-size URL shown in the lightbox. Defaults to `src`. */
  previewSrc?: string
  /** Optional caption shown under the enlarged image. */
  caption?: string
  /** If false, disables the lightbox (image still renders). Default: true. */
  zoomable?: boolean
  /** When provided, lightbox shows arrows to navigate the full list. */
  gallery?: GalleryItem[]
  /** Index of this image within `gallery`. Required when `gallery` is set. */
  galleryIndex?: number
}

export function ImagePreview({
  previewSrc,
  caption,
  zoomable = true,
  gallery,
  galleryIndex = 0,
  className,
  onError,
  onClick,
  src,
  alt,
  ...imgProps
}: ImagePreviewProps) {
  const [open, setOpen] = useState(false)
  const [failed, setFailed] = useState(false)
  const [current, setCurrent] = useState(galleryIndex)

  const enlargedSrc = previewSrc ?? src
  const canZoom = zoomable && !failed && Boolean(enlargedSrc)

  const isGallery = gallery && gallery.length > 1

  // Sync current index when the lightbox opens
  useEffect(() => {
    if (open) {setCurrent(galleryIndex)}
  }, [open, galleryIndex])

  const prev = useCallback(() => {
    if (!gallery) {return}
    setCurrent((i) => (i - 1 + gallery.length) % gallery.length)
  }, [gallery])

  const next = useCallback(() => {
    if (!gallery) {return}
    setCurrent((i) => (i + 1) % gallery.length)
  }, [gallery])

  useEffect(() => {
    if (!open || !isGallery) {return}
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {prev()}
      else if (e.key === 'ArrowRight') {next()}
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, isGallery, prev, next])

  const handleError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    setFailed(true)
    onError?.(e)
  }

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    onClick?.(e)
    if (canZoom && !e.defaultPrevented) {setOpen(true)}
  }

  const activeItem = isGallery ? gallery[current] : null
  const displaySrc = activeItem?.src ?? (enlargedSrc as string)
  const displayAlt = activeItem?.alt ?? alt
  const displayCaption = activeItem?.caption ?? caption

  return (
    <>
      <img
        {...imgProps}
        src={src}
        alt={alt}
        onError={handleError}
        onClick={handleClick}
        className={cn(
          failed && 'hidden',
          canZoom && 'cursor-zoom-in transition-opacity hover:opacity-90',
          className,
        )}
      />

      {canZoom && (
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
            <Dialog.Content
              className="fixed left-1/2 top-1/2 z-[9999] flex max-h-[92vh] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
              onClick={() => setOpen(false)}
            >
              <Dialog.Title className="sr-only">{displayAlt || displayCaption || 'Image preview'}</Dialog.Title>

              {/* Image */}
              <img
                key={displaySrc}
                src={displaySrc}
                alt={displayAlt}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[85vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
              />

              {/* Caption */}
              {displayCaption && (
                <p className="max-w-[80vw] truncate text-center text-sm font-medium text-white/90">
                  {displayCaption}
                </p>
              )}

              {/* Prev / Next arrows */}
              {isGallery && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    onClick={(e) => { e.stopPropagation(); prev() }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/70 text-gray-800 backdrop-blur-sm transition-colors hover:bg-white/90 focus:outline-none"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    onClick={(e) => { e.stopPropagation(); next() }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/70 text-gray-800 backdrop-blur-sm transition-colors hover:bg-white/90 focus:outline-none"
                  >
                    <ChevronRight size={22} />
                  </button>

                  {/* Counter */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                    {current + 1} / {gallery.length}
                  </div>
                </>
              )}

              {/* Close */}
              <Dialog.Close
                className="absolute right-2 top-2 rounded-full border border-white/60 bg-white/70 p-2 text-gray-800 backdrop-blur-sm transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="Close preview"
              >
                <X size={20} />
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </>
  )
}
